import {
  FetchResult,
  MutationFunctionOptions,
  OperationVariables,
  QueryResult,
} from '@apollo/client';
import { isEmpty, isNil, omitBy } from 'lodash';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { BaseAsset } from '../types';
import {
  AssetDetails,
  BaseAssetManagerContext,
  BaseAssetManagerContextState,
} from './BaseAssetManagerContext';

const TOAST_TIMEOUT = 5000; // 5 Seconds
const NEW_ASSET = { isNewAsset: true };

type QueryHookResult = Partial<QueryResult<BaseAsset[]>>;

type CreateUpdateAssetHook = (
  onComplete: (asset: BaseAsset) => void,
  onError: (errorMessage: string) => void,
) => (
  options?: MutationFunctionOptions<any, OperationVariables> | undefined,
) => Promise<FetchResult<any, Record<string, any>, Record<string, any>>>;

type DeleteUpdateAssetHook = (
  onComplete: () => void,
  onError: (errorMessage: string) => void,
) => (
  options?: MutationFunctionOptions<any, OperationVariables> | undefined,
) => Promise<FetchResult<any, Record<string, any>, Record<string, any>>>;

type AssetContextProviderProps = {
  children?: ReactNode;
  assetSource: () => QueryHookResult;
  assetTypeName?: string;
  // WARNING: I'm pretty sure that passing through hooks in this way violates
  // the rules of hooks
  createAssetHook?: CreateUpdateAssetHook;
  updateAssetHook?: CreateUpdateAssetHook;
  deleteAssetHook?: DeleteUpdateAssetHook;
  validateAsset?: (details: AssetDetails<BaseAsset>) => any;
  initialSelectedId?: number | string | undefined;
};

export const BaseAssetManagerContextProvider = ({
  children,
  assetSource,
  assetTypeName,
  createAssetHook,
  updateAssetHook,
  deleteAssetHook,
  validateAsset,
  initialSelectedId = undefined,
}: AssetContextProviderProps) => {
  const dispatch = useDispatch();

  const { data: assetList, refetch } = assetSource();
  const sortedAssetList = useMemo(
    () =>
      assetList &&
      [...assetList].sort((a: BaseAsset, b: BaseAsset) => {
        const nameA = a.name?.toLowerCase() || '';
        const nameB = b.name?.toLowerCase() || '';
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;

        if (!a.createdAt || !b.createdAt) return 0;
        const createdAtA = new Date(a.createdAt).getTime();
        const createdAtB = new Date(b.createdAt).getTime();
        if (createdAtA < createdAtB) return -1;
        if (createdAtA > createdAtB) return 1;

        return 0;
      }),
    [assetList],
  );

  const [searchedAssets, setSearchedAssets] = useState<BaseAsset[]>(sortedAssetList || []);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [changesMade, setChangesMade] = useState<boolean>(false);

  const [assetDetails, setAssetDetails] = useState<AssetDetails<BaseAsset>>({});
  const [selectedAssetId, setSelectedAssetId] = useState<number | string | undefined>(
    initialSelectedId,
  );
  const [assetErrors, setAssetErrors] = useState({});

  /** Effect should only run based on changing data like updates and creates,
   * also sets values after data on initial load */
  useEffect(() => {
    /** Upon initial load, sortedAssetList is not yet defined so we do not want
     * to evaluate assetDetails until the list of assets has been fetched */
    if (sortedAssetList) {
      const selectedAsset = selectedAssetId
        ? sortedAssetList.find((asset) => asset.id === selectedAssetId)
        : null;
      const asset =
        (!isEmpty(assetDetails) && assetDetails) ||
        selectedAsset ||
        sortedAssetList[0] ||
        NEW_ASSET;
      setSearchedAssets(sortedAssetList || []);
      setAssetDetails(asset);
      setSelectedAssetId(selectedAssetId || asset?.id);
    }
  }, [sortedAssetList]);

  const update = updateAssetHook?.(
    (updatedAsset) => {
      setIsEditing(false);
      setAssetDetails(updatedAsset);
      setChangesMade(false);
      dispatch(addPositiveToast(`${assetTypeName} saved`, { timeout: TOAST_TIMEOUT }));
    },
    (errorMessage) => {
      dispatch(addNegativeToast(errorMessage, { timeout: TOAST_TIMEOUT }));
    },
  );

  const create = createAssetHook?.(
    (newAsset) => {
      setIsEditing(false);
      setAssetDetails(newAsset);
      setSelectedAssetId(newAsset.id);
      setChangesMade(false);
      dispatch(addPositiveToast(`${assetTypeName} created`, { timeout: TOAST_TIMEOUT }));
    },
    (errorMessage) => {
      dispatch(addNegativeToast(errorMessage, { timeout: TOAST_TIMEOUT }));
    },
  );

  const deleteAsset = deleteAssetHook?.(
    () => {
      setIsEditing(false);
      setSelectedAssetId(undefined);
      setAssetDetails({});
      setChangesMade(false);
      dispatch(addPositiveToast(`Success! Contact card deleted.`, { timeout: TOAST_TIMEOUT }));
      refetch?.();
    },
    (errorMessage) => {
      dispatch(addNegativeToast(errorMessage, { timeout: TOAST_TIMEOUT }));
    },
  );

  const handleSave = useCallback(
    (transformToInput?: (assetDetails: AssetDetails<BaseAsset>) => any) => {
      if (isEditing) {
        const errors = validateAsset?.(assetDetails);
        const inputVariables = transformToInput?.(assetDetails) ?? assetDetails;
        const cleanedFields = omitBy(inputVariables, isNil);

        if (!isEmpty(errors)) {
          setAssetErrors(errors);
        } else if (assetDetails.isNewAsset) {
          create?.({
            variables: cleanedFields,
          });
        } else {
          update?.({
            variables: {
              id: assetDetails.id,
              fields: cleanedFields,
            },
          });
          setAssetErrors({});
        }
      }
    },
    [assetDetails, isEditing],
  );

  const handleSearch = useCallback(
    (searchText: string) => {
      setSearchedAssets(
        sortedAssetList?.filter((asset) =>
          Object.values(asset).some((val: any) =>
            val?.toString().toLowerCase()?.includes(searchText.toLowerCase()),
          ),
        ) || [],
      );
    },
    [sortedAssetList],
  );

  const handleSelect = (selected?: Partial<BaseAsset>) => {
    if (selected) {
      setAssetDetails(selected);
      setSelectedAssetId(selected.id);
      setAssetErrors({});
    }
  };

  const handleDeleteAsset = useCallback(
    (id?: number | string) => {
      if (id) {
        deleteAsset?.({ variables: { id } });
      }
    },
    [deleteAsset],
  );

  const handleClose = useCallback(
    (onClose: any) => {
      if (!changesMade) {
        onClose();
        handleSelect(sortedAssetList?.[0]);
      } else {
        dispatch(
          addNegativeToast('Unsaved changes. Please save or cancel to proceed.', {
            timeout: TOAST_TIMEOUT,
          }),
        );
      }
    },
    [changesMade, sortedAssetList],
  );

  const handleCreateNew = () => {
    setAssetDetails(NEW_ASSET);
    setSelectedAssetId(undefined);
    setAssetErrors({});
    setIsEditing(true);
  };

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setChangesMade(false);
    setAssetErrors({});

    if (sortedAssetList?.length === 0 || !sortedAssetList) {
      setIsModalOpen(false);
      return setAssetDetails(NEW_ASSET);
    }

    const originalAsset = sortedAssetList?.find((asset) => asset.id === selectedAssetId);
    if (originalAsset) return setAssetDetails(originalAsset);

    handleSelect(sortedAssetList?.[0]);
  }, [selectedAssetId, sortedAssetList]);

  const context: BaseAssetManagerContextState<BaseAsset> = {
    assets: searchedAssets,
    assetTypeName,
    isModalOpen,
    isEditing,
    changesMade,
    selectedAssetId,
    assetDetails,
    assetErrors,
    onSearch: handleSearch,
    selectAssetInList: handleSelect,
    handleClose,
    createNew: handleCreateNew,
    save: handleSave,
    deleteAsset: handleDeleteAsset,
    cancel: handleCancel,
    setIsModalOpen,
    setIsEditing,
    setChangesMade,
    updateAssetDetails: setAssetDetails,
    updateAssetErrors: setAssetErrors,
  };

  return (
    <BaseAssetManagerContext.Provider value={context}>{children}</BaseAssetManagerContext.Provider>
  );
};
